<template>
  <div
    :id="id"
    ref="modal"
    :class="modalClass"
    tabindex="-1"
    role="dialog"
    :style="{ display: shown ? 'block' : 'none' }"
    :aria-labelledby="ariaLabelledById"
    :aria-hidden="!shown"
    aria-modal="true"
    @keydown.esc="handleEscape"
    @click.self="onBackdropClick"
  >
    <div
      class="modal-dialog"
      role="document"
      :class="modalDialogClass"
    >
      <div :class="['modal-content', { 'modal-content--with-image': withImage }]">
        <!-- Close Button -->
        <button
          v-if="!noClose"
          ref="closeButton"
          type="button"
          class="modal-close"
          data-dismiss="modal"
          :aria-label="i18n.close"
          @click="close"
        >
          <svg-close />
        </button>

        <!-- Modal Image -->
        <div
          v-if="withImage"
          :class="['modal-image', { 'modal-image--with-error': imageError }]"
        >
          <div
            v-if="$slots['image-top-left-slot']"
            class="modal-image__top-left-slot"
          >
            <slot name="image-top-left-slot"></slot>
          </div>
          <svg-profile-media-placeholder
            v-if="imageError"
            :size="100"
          />
          <img
            v-else
            :src="image.src"
            :width="image.width || imageDefaultWidth"
            :height="image.height || imageDefaultHeight"
            :style="imageStyles"
            @error="imageError = true"
          />
        </div>
        <!-- Modal Header -->
        <div
          v-if="$slots.header"
          class="modal-header"
        >
          <div
            :id="ariaLabelledById"
            :class="['modal-title', { 'modal-title--with-subtitle': $slots.subtitle }]"
          >
            <slot name="header"></slot>
          </div>
        </div>
        <!-- Modal Subtitle -->
        <div
          v-if="$slots.subtitle"
          class="modal-subtitle"
        >
          <slot name="subtitle"></slot>
        </div>
        <!-- Modal carousel -->
        <ui-bootstrap-carousel
          v-if="$slots.carousel"
          :id="`${id}-carousel`"
          class="modal-carousel"
          :auto-slide="carousel.autoSlide"
          :with-indicators="carousel.withIndicators"
          :buttons-appearance="buttonsAppearance"
        >
          <!-- ⚠️ Always put carousel template childrens in a single parent div element -->
          <template #carousel>
            <slot name="carousel"></slot>
          </template>
        </ui-bootstrap-carousel>
        <template v-else>
          <!-- Modal Icon -->
          <div
            v-if="$slots.icon"
            class="modal-icon"
          >
            <slot name="icon"></slot>
          </div>
          <!-- Modal Body -->
          <div
            v-if="$slots.body"
            class="modal-body"
          >
            <slot name="body"></slot>
          </div>
        </template>
        <!-- Modal Footer -->
        <div
          v-if="$slots.footer"
          :class="[
            'modal-footer',
            {
              'modal-footer--no-wrap': footerNoWrap,
              'd-none': noConfirm && noCancel && !$slots.footer,
            },
          ]"
        >
          <slot name="footer"></slot>
        </div>
        <div
          v-else
          :class="[
            'modal-footer',
            { 'modal-footer--no-wrap': footerNoWrap, 'd-none': noConfirm && noCancel },
          ]"
        >
          <ui-button
            v-if="!noConfirm"
            ref="confirmButton"
            :disabled="disableConfirm"
            block
            uppercase
            :appearance="buttonsAppearance"
            font-weight="semibold"
            @click="confirm"
          >
            {{ confirmText }}
          </ui-button>
          <ui-button
            v-if="!noCancel"
            ref="cancelButton"
            data-dismiss="modal"
            :disabled="disableCancel"
            block
            uppercase
            font-weight="semibold"
            :appearance="buttonsAppearance"
            button-type="blank"
            @click="cancel"
          >
            {{ cancelText }}
          </ui-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  // import Translations from "./insert-me-to-translate.vue";

  import {
    blockScroll,
    releaseScroll,
    buttonAppearanceValidator,
    modalSizeValidator,
    modalImagePropsValidator,
    imagePositionValidator,
  } from '../../utils'

  export default {
    components: {
      UiButton: () => import(/* webpackChunkName: "chunk-ui-button" */ './ui-button.vue'),
      SvgClose: () => import(/* webpackChunkName: "chunk-svg" */ '../svg/close.vue'),
      SvgProfileMediaPlaceholder: () =>
        import(
          /* webpackChunkName: "chunk-svg-profile-media-placeholder" */ '../svg/profile-media-placeholder.vue'
        ),
      UiBootstrapCarousel: () =>
        import(
          /* webpackChunkName: "chunk-ui-bootstrap-carousel" */ './bootstrap-carousel/ui-bootstrap-carousel.vue'
        ),
    },
    props: {
      id: {
        type: String,
        required: true,
      },
      shown: {
        type: Boolean,
        default: false,
      },
      alignCenter: {
        type: Boolean,
        default: false,
      },
      size: {
        type: String,
        default: 'md',
        validator: modalSizeValidator,
      },
      noClose: {
        type: Boolean,
        default: false,
      },
      image: {
        type: Object,
        default: null,
        validator: modalImagePropsValidator,
      },
      buttonsAppearance: {
        type: String,
        default: 'secondary',
        validator: buttonAppearanceValidator,
      },

      // Cancel
      handleCancel: {
        type: Function,
        default: null,
      },
      disableCancel: {
        type: Boolean,
        default: false,
      },
      labelCancel: {
        type: String,
        default: gettext('Cancel'),
      },
      noCancel: {
        type: Boolean,
        default: false,
      },

      // Confirm
      handleConfirm: {
        type: Function,
        default: null,
      },
      disableConfirm: {
        type: Boolean,
        default: false,
      },
      labelConfirm: {
        type: String,
        default: gettext('Confirm'),
      },
      noConfirm: {
        type: Boolean,
        default: false,
      },
      footerNoWrap: {
        type: Boolean,
        default: false,
      },
      noBackdrop: {
        type: Boolean,
        default: true,
      },

      handleClose: {
        type: Function,
        default: null,
      },

      carousel: {
        type: Object,
        default: () => ({ withIndicators: false, autoSlide: true }),
        validator(value) {
          return (typeof value.withIndicators === 'boolean', typeof value.autoSlide === 'boolean')
        },
      },
      imagePosition: {
        type: String,
        default: 'center center',
        validator: imagePositionValidator,
      },
    },
    data() {
      return {
        i18n: {
          close: gettext('Close'),
        },
        // Focus management
        focusableElements: [],
        previousActiveElement: null,
        firstFocusableElement: null,
        lastFocusableElement: null,

        // Image management
        imageDefaultWidth: 360,
        imageDefaultHeight: 160,
        imageDefaultPosition: 'center center',
        imageError: false,
      }
    },
    computed: {
      confirmText() {
        return this.labelConfirm
      },
      cancelText() {
        return this.labelCancel
      },
      ariaLabelledById() {
        return `${this.id}Label`
      },
      modalClass() {
        return {
          'modal ui-modal fade': true,
          show: this.shown,
          'ui-modal--align-center': this.alignCenter,
          'ui-modal--with-image': this.withImage,
          'ui-modal--with-carousel': this.$slots.carousel,
        }
      },
      modalDialogClass() {
        return {
          'modal-dialog-centered': true,
          [`modal-${this.size}`]: this.size,
        }
      },
      withImage() {
        return !!this.image?.src
      },
      imageStyles() {
        if (!this.withImage) return {}

        let styles = {
          '--ui-modal-image-position': this.imagePosition,
        }
        if (this.image?.height) {
          styles['--ui-modal-image-height'] = this.image.height
        }
        return styles
      },
    },
    watch: {
      shown: {
        immediate: false,
        handler(newVal) {
          if (newVal) {
            this.openModal()
          } else {
            this.closeModal()
          }
        },
      },
      'image.src': function () {
        this.imageError = false
      },
    },
    beforeDestroy() {
      this.cleanup()
    },
    methods: {
      async confirm(event) {
        if (this.handleConfirm && typeof this.handleConfirm === 'function') {
          try {
            // If handleConfirm is not async, it runs normally.
            await this.handleConfirm(event)
          } catch (error) {
            console.error('Error in handleConfirm:', error)
            return
          }
        }
        this.$emit('confirm', event)
      },
      async cancel(event) {
        if (this.handleCancel && typeof this.handleCancel === 'function') {
          try {
            // If handleCancel is not async, it runs normally.
            await this.handleCancel(event)
          } catch (error) {
            console.error('Error in handleConfirm:', error)
            return
          }
        }
        this.$emit('cancel', event)
      },
      async close(event) {
        if (this.handleClose && typeof this.handleClose === 'function') {
          try {
            // If handleClose is not async, it runs normally.
            await this.handleClose(event)
          } catch (error) {
            console.error('Error in handleClose:', error)
            return
          }
        }
        this.$emit('close', event)
      },
      handleEscape(event) {
        if (!this.noClose) {
          event.preventDefault()
          this.close(event)
        }
      },
      toggleBackdrop(visible) {
        const existingBackdrop = document.querySelector('.modal-backdrop')

        if (visible) {
          if (!existingBackdrop) {
            const backdrop = document.createElement('div')
            backdrop.className = 'modal-backdrop fade show'
            document.body.appendChild(backdrop)
          }
        } else {
          if (existingBackdrop) {
            existingBackdrop.remove()
          }
        }
      },
      onBackdropClick() {
        if (!this.noClose) {
          this.close()
        }
      },
      openModal() {
        blockScroll()
        if (!this.noBackdrop) {
          this.toggleBackdrop(true)
        }

        this.$nextTick(() => {
          this.setFocusTrap()
        })
      },

      closeModal() {
        this.removeFocusTrap()
        releaseScroll()
        if (!this.noBackdrop) {
          this.toggleBackdrop(false)
        }
      },
      cleanup() {
        if (this.shown) {
          this.closeModal()
        }
        this.removeFocusTrap()
      },
      // Gets all focusable elements within the modal (not disable and actually visible)
      getFocusableElements() {
        const selectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        const elements = this.$refs.modal.querySelectorAll(selectors)
        // offsetParent returns the nearest positioned ancestor element (an element with a CSS position value other than "static")
        return Array.from(elements).filter((el) => !el.disabled && el.offsetParent !== null)
      },
      // Sets up the focus trap when modal opens
      setFocusTrap() {
        // Store the element that had focus before modal opened
        this.previousActiveElement = document.activeElement

        this.focusableElements = this.getFocusableElements()
        this.firstFocusableElement = this.focusableElements.length
          ? this.focusableElements[0]
          : null
        this.lastFocusableElement = this.focusableElements.length
          ? this.focusableElements[this.focusableElements.length - 1]
          : null

        // Add event listener for tab key
        this.$refs.modal.addEventListener('keydown', this.handleTabKey)

        // Set initial focus
        this.$nextTick(() => {
          // Focus the close button if available, otherwise the first focusable element
          const initialFocus = this.$refs.closeButton || this.firstFocusableElement
          if (initialFocus) initialFocus.focus()
        })
      },
      //  Cleans up focus trap when modal closes
      removeFocusTrap() {
        this.$refs.modal.removeEventListener('keydown', this.handleTabKey)

        // Return focus to the element that had it before modal opened
        if (this.previousActiveElement) this.previousActiveElement.focus()
      },
      // Handles tab key navigation within the modal
      handleTabKey(event) {
        if (event.key !== 'Tab') return

        if (event.shiftKey) {
          // shift + tab keys
          if (document.activeElement === this.firstFocusableElement) {
            event.preventDefault()
            if (this.lastFocusableElement) this.lastFocusableElement.focus()
          }
        } else {
          // tab key
          if (document.activeElement === this.lastFocusableElement) {
            event.preventDefault()
            if (this.firstFocusableElement) this.firstFocusableElement.focus()
          }
        }
      },
    },
  }
</script>
